import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import {
  LEGACY_UTM_STORAGE_KEY,
  PENDING_VISIT_KEY,
} from '../constants/storage-keys.constants';
import { Cookie } from '../services/cookie';
import { Platform } from '../services/platform';
import { AuthApi } from '../../features/auth/services/auth.api';
import { AuthState } from '../../features/auth/services/auth.state';
import { CampaignVisit } from '../../features/auth/models/auth.model';
import { daysFromNow, ensureVisitorUuid } from './visitor';
import {
  pagePath,
  readCampaignParams,
  sameVisit,
  stripCampaignParams,
  toPendingVisit,
} from './utm';

const PENDING_VISIT_MAX_AGE_DAYS = 30;

/**
 * Registra el clic de campaña de la URL (`cid` y parámetros de anuncio). Si
 * el envío falla, queda pendiente en cookie y se reintenta en la siguiente carga.
 */
export function captureCampaignVisit(): void {
  const platform = inject(Platform);
  const cookie = inject(Cookie);
  const authApi = inject(AuthApi);
  const authState = inject(AuthState);
  if (!platform.isBrowser) return;

  cookie.delete(LEGACY_UTM_STORAGE_KEY);
  const visitorUuid = ensureVisitorUuid(cookie);
  if (!visitorUuid) return;

  const current = new URL(window.location.href);
  const params = readCampaignParams(current.searchParams);
  if (params) {
    const ruta = pagePath(current.pathname);
    const visit: CampaignVisit = {
      visitante_uuid: visitorUuid,
      ...params,
      ...(ruta && { ruta }),
    };
    cookie.set(
      PENDING_VISIT_KEY,
      visit,
      daysFromNow(PENDING_VISIT_MAX_AGE_DAYS)
    );
    window.history.replaceState(
      window.history.state,
      '',
      stripCampaignParams(current).href
    );
    sendVisit(visit);
    return;
  }

  const pending = toPendingVisit(cookie.get(PENDING_VISIT_KEY));
  if (pending) sendVisit(pending);

  function sendVisit(visit: CampaignVisit): void {
    authApi.registerCampaignVisit(visit).subscribe({
      next: () => {
        clearPending(visit);
        // Una sesión previa a la cookie del visitante aún no tiene su uuid en el contacto.
        if (authState.isLoggedIn()) authState.identifyCampaignContact();
      },
      error: (error: HttpErrorResponse) => {
        // Datos rechazados por el backend: reintentar no cambia el resultado.
        if (error.status >= 400 && error.status < 500 && error.status !== 429) {
          clearPending(visit);
        }
      },
    });
  }

  function clearPending(visit: CampaignVisit): void {
    const latest = toPendingVisit(cookie.get(PENDING_VISIT_KEY));
    if (latest && sameVisit(latest, visit)) cookie.delete(PENDING_VISIT_KEY);
  }
}
